/**
 * The colours already in a design, and the ones chosen lately.
 *
 * The most common colour decision on a business card is not "which colour" but
 * "the same one as that": the heading the colour of the logo, the rule the
 * colour of the heading. Offering the design's own colours first makes that one
 * click, and makes it the same ink rather than one that looks the same on
 * screen and prints a shade apart.
 */

import type { Colour, Ink, Layer, TrimboxDocument } from '../model/types';
import { isGradient } from '../model/types';

/** Enough to cover a card, few enough to read at a glance. */
const MOST_USED = 12;
const MOST_RECENT = 8;

const KEY = 'trimbox-recent-colours';

/** One string per ink, so the same colour set twice is listed once. */
export function inkKey( colour: Colour ): string {
  if ( colour.space === 'rgb' ) return colour.value.toLowerCase();
  if ( colour.space === 'spot' ) return `spot:${ colour.value }`;

  return colour.value.map( ( v ) => Math.round( v * 1000 ) ).join( '/' );
}

/** Every distinct colour in the design, most used first. */
export function coloursIn( doc: TrimboxDocument | null ): Colour[] {
  if ( ! doc ) return [];

  const seen = new Map<string, { colour: Colour; count: number }>();

  const add = ( colour: Colour | null | undefined ) => {
    if ( ! colour ) return;

    // Paper is not a colour anybody chooses to match.
    if ( colour.space === 'cmyk' && colour.value.every( ( v ) => v === 0 ) ) return;

    const key = inkKey( colour );
    const had = seen.get( key );

    if ( had ) had.count++;
    else seen.set( key, { colour, count: 1 } );
  };

  const walk = ( layers: Layer[] ) => {
    for ( const layer of layers ) {
      add( layer.shadow?.colour );

      switch ( layer.type ) {
        case 'text':
          if ( isGradient( layer.fill ) ) layer.fill.stops.forEach( ( stop ) => add( stop.colour ) );
          else add( layer.fill );
          break;
        case 'shape':
          if ( isGradient( layer.fill ) ) layer.fill.stops.forEach( ( stop ) => add( stop.colour ) );
          else add( layer.fill );

          add( layer.stroke?.colour );
          break;
        case 'qr':
          add( layer.fill );
          break;
        case 'barcode':
          add( layer.fill );
          break;
        case 'group':
          walk( layer.children );
          break;
        default:
      }
    }
  };

  for ( const page of doc.pages ) {
    if ( page.background?.type === 'colour' ) add( page.background.value );

    walk( page.layers );
  }

  return [ ...seen.values() ]
    .sort( ( a, b ) => b.count - a.count )
    .slice( 0, MOST_USED )
    .map( ( entry ) => entry.colour );
}

/**
 * Colours chosen lately, newest first.
 *
 * In the browser's own storage rather than the account: a preference about
 * this machine and this afternoon, not something worth a round trip or a
 * column in a table.
 */
export function recentColours(): Colour[] {
  try {
    const raw = window.localStorage.getItem( KEY );
    const list = raw ? JSON.parse( raw ) : [];

    return Array.isArray( list ) ? list.filter( isColour ).slice( 0, MOST_RECENT ) : [];
  } catch {
    // Private browsing, a full quota, or a value some other version wrote.
    return [];
  }
}

export function rememberColour( colour: Colour | null ) {
  if ( ! colour ) return;

  try {
    const key = inkKey( colour );
    const list = [ colour ].concat( recentColours().filter( ( other ) => inkKey( other ) !== key ) );

    window.localStorage.setItem( KEY, JSON.stringify( list.slice( 0, MOST_RECENT ) ) );
  } catch {
    // Not remembering is the whole of the failure.
  }
}

function isColour( value: unknown ): value is Colour {
  if ( ! value || typeof value !== 'object' ) return false;

  const colour = value as { space?: unknown; value?: unknown; fallback?: unknown };

  if ( colour.space === 'rgb' ) return typeof colour.value === 'string';
  if ( colour.space === 'cmyk' ) return isInk( colour.value );
  if ( colour.space === 'spot' ) return typeof colour.value === 'string' && isInk( colour.fallback );

  return false;
}

function isInk( value: unknown ): value is Ink {
  return Array.isArray( value ) && value.length === 4 && value.every( ( v ) => typeof v === 'number' && v >= 0 && v <= 1 );
}
